import CircularProgress from "@mui/material/CircularProgress";
import { CustomRenderInputParams, GenerateRenderOptionParams, RenderOptionLIElement } from "./types";
import { AutocompleteRenderInputParams, AutocompleteRenderOptionState, AutocompleteOwnerState, Tooltip } from "@mui/material";
import { HTMLAttributes, Key } from "react";

/**
    @description - Add the loading spinner when loading to the rendered input component
*/

const generateRenderInput = ({ renderInput, loading, dependency, tooltipMessage, haveRetrievedAll, value, disabled, loadingProp }: CustomRenderInputParams) => {
  // Only show placeholder if NOT multiple and no value
  const valueIsArray = Array.isArray(value);

  const showPlaceholderBasedOnValue = (!valueIsArray && !value) || valueIsArray ? true : false;

  return (params: AutocompleteRenderInputParams) => {
    const modifiedInputProps: AutocompleteRenderInputParams = {
      ...params,
      InputProps: {
        ...params.InputProps,
        endAdornment: (
          <>
            {loading || loadingProp ? <CircularProgress size={20} /> : null}
            {params.InputProps.endAdornment}
          </>
        ),
      },
      inputProps: {
        ...params.inputProps,
        placeholder: !haveRetrievedAll && !loading && showPlaceholderBasedOnValue ? (!disabled ? "Type to Search" : "") : params.inputProps?.placeholder || undefined,
      },
    };

    const useTooltip = dependency && !dependency?.fulfilled && dependency.tooltipMessage;

    const Input = renderInput(modifiedInputProps);

    if (useTooltip) {
      return (
        <Tooltip title={dependency.tooltipMessage}>
          {/* @ts-ignore - this is a valid component to render as a child */}
          {Input}
        </Tooltip>
      );
    } else if (tooltipMessage) {
      return (
        <Tooltip title={tooltipMessage}>
          {/* @ts-ignore - this is a valid component to render as a child */}
          {Input}
        </Tooltip>
      );
    }

    return Input;
  };
};

/**
    @description - This will add the "key" prop to each rendered option which uses the id of the record so if multiple records 
        have the same value - they will still have a unique key
*/

const generateRenderOption = ({ renderOption, isAllSelected }: GenerateRenderOptionParams) => {
  // MUI v7 types the incoming key as React.Key - it is replaced with our own string key below
  return (
    props: HTMLAttributes<HTMLLIElement> & { key: Key },
    option: any,
    state: AutocompleteRenderOptionState,
    ownerState: AutocompleteOwnerState<any, boolean, boolean, boolean, "div">,
  ) => {
    const modifiedRenderOptionProps: RenderOptionLIElement = {
      ...props,
      key: `${option.autocompleteLabel} - ${option.id}`,
    };

    if (renderOption) {
      const Option = renderOption(modifiedRenderOptionProps, option, state, ownerState, isAllSelected);

      return Option;
    }

    const renderCustomOption = (props: any, option: any, { selected }: any) => (
      <li {...props} key={`${option.autocompleteLabel} - ${option.id}`} data-selected={selected}>
        {option.autocompleteLabel}
      </li>
    );

    const Option = renderCustomOption(modifiedRenderOptionProps, option, state);

    return Option;
  };
};

export { generateRenderInput, generateRenderOption };
